import { Link } from "react-router-dom";
import { AlertTriangle, PhoneCall, ShieldAlert, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const GATES = [
  "Prescription medicines — many supplements interact (blood thinners and omega-3 are the classic example).",
  "Kidney, liver or heart conditions — protein, magnesium, creatine and vitamin D all change meaning here.",
  "Bleeding disorders or upcoming surgery — several common supplements must stop beforehand.",
  "Unexplained symptoms — fatigue, weight change or pain are clinician conversations, not cart contents.",
  "Recent abnormal labs — supplements can mask or distort what your doctor is tracking.",
];

const SEE_DOCTOR = [
  "Fatigue that persists for weeks despite sleep and food changes",
  "Loud snoring, gasping at night, or severe daytime sleepiness",
  "Chest pain, dizziness or breathlessness with exertion",
  "Unintentional weight loss or gain",
  "Low mood, or anything that simply feels wrong and stays wrong",
];

export default function Safety() {
  return (
    <main className="min-h-screen pt-28 pb-24 px-4 sm:px-6" data-testid="safety-page">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-clay mb-4">safety gates</p>
          <h1 className="font-serif text-4xl sm:text-6xl font-extrabold uppercase tracking-tight leading-[0.95]" data-testid="safety-title">
            The gate comes first. <span className="italic text-sage">The plan comes second.</span>
          </h1>
          <p className="mt-5 text-slatem leading-relaxed max-w-2xl">
            One honest answer in the check-in flips your entire plan into clinician-safe mode. Here is exactly what that means and when to go further.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-12 rounded-3xl border border-clay/40 bg-clay/5 p-7 sm:p-9" data-testid="safety-gate-explainer">
            <div className="flex items-center gap-3 mb-5">
              <ShieldAlert className="h-6 w-6 text-clay" />
              <h2 className="font-serif text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">Clinician-safe mode</h2>
            </div>
            <p className="text-sm sm:text-base text-ink/80 leading-relaxed mb-5">
              If you flag any of the following, your plan keeps its food, movement and sleep foundations — and every
              supplement suggestion is removed. Not greyed out. Removed.
            </p>
            <ul className="space-y-3">
              {GATES.map((g) => (
                <li key={g} className="flex items-start gap-3 text-sm text-ink/80 leading-relaxed">
                  <span className="h-1.5 w-1.5 rounded-full bg-clay shrink-0 mt-2" />{g}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-6 rounded-3xl border border-ink/12 bg-bone p-7 sm:p-9" data-testid="safety-doctor-list">
            <div className="flex items-center gap-3 mb-5">
              <PhoneCall className="h-6 w-6 text-sage" />
              <h2 className="font-serif text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">Speak to a doctor when…</h2>
            </div>
            <ul className="space-y-3">
              {SEE_DOCTOR.map((g) => (
                <li key={g} className="flex items-start gap-3 text-sm text-ink/80 leading-relaxed">
                  <span className="h-1.5 w-1.5 rounded-full bg-sage shrink-0 mt-2" />{g}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-6 rounded-3xl bg-ink text-bone p-7 sm:p-9 zb-grain relative overflow-hidden" data-testid="safety-disclaimer-block">
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck className="h-6 w-6 text-sage" />
              <h2 className="font-serif text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">The standing disclaimer</h2>
            </div>
            <p className="text-sm sm:text-base text-bone/80 leading-relaxed mb-4">
              Zinda Banda provides general wellbeing information only — not medical advice, diagnosis, or treatment.
              Supplements can cause side effects and interact with medicines. Check with a qualified doctor, dietitian
              or pharmacist if you have symptoms, a health condition, take medicine, are preparing for surgery, or are unsure.
            </p>
            <p className="text-sm text-bone/70 leading-relaxed flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 text-clay shrink-0 mt-0.5" />
              Emergencies: in the UAE call 998 (ambulance); in India call 112. Do not use this site in an emergency.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <p className="mt-10 text-center text-sm text-slatem">
            Curious how the gate changes a real result?{" "}
            <Link to="/check-in" data-testid="safety-cta" className="font-semibold text-clay hover:underline">Take the test</Link>
          </p>
        </Reveal>
      </div>
    </main>
  );
}
